/**
 * Paginated list of bulk RMA action requests for one store.
 */
// eslint-disable-next-line
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { rootContainer, TYPE } from '../../../../../services/container/root';
import PagedDataStore, { IFetchListArgs, IPagedData } from '../../../../../stores/abstract-paged-data-store';
import { BULK_REQUEST_STATUS } from '../../../../../services/api/bulk-rma-action-types';
import TroubleshootingService, {
    IBulkRmaActionPaginatedRows,
    IBulkRmaActionRequestSummary,
} from '../../../../../services/api/troubleshooting-service';

const UNRESOLVED_COUNT_KEYS = ['pending', 'allocated', 'processing'] as const;

export class BulkRmaActionRequestsPagedListStore extends PagedDataStore<IBulkRmaActionRequestSummary> {
    readonly perPage: number = 20;

    @observable storeId: string | number | null = null;
    @observable statusFilter: BULK_REQUEST_STATUS | '' = '';
    @observable error: string | null = null;

    constructor() {
        super();
        makeObservable(this);
    }

    private get troubleshootingService(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    @computed
    get unresolvedReturns(): Record<number, number> {
        return Object.fromEntries(
            this.list.map((request) => [
                request.request_id,
                UNRESOLVED_COUNT_KEYS.reduce(
                    (sum, key) => sum + (request.counts[key] ?? 0),
                    0,
                ),
            ]),
        );
    }

    @action
    setStoreId(storeId: string | number | null): void {
        this.storeId = storeId;
    }

    @action
    setStatusFilter(status: BULK_REQUEST_STATUS | ''): void {
        this.statusFilter = status;
    }

    @action
    clearError(): void {
        this.error = null;
    }

    private mapRequestsPageToPagedData(
        page: IBulkRmaActionPaginatedRows<IBulkRmaActionRequestSummary>,
    ): IPagedData<IBulkRmaActionRequestSummary> {
        const lastPage = page.last_page ?? Math.max(1, Math.ceil(page.total / page.per_page));
        const from = page.total === 0 ? 0 : (page.current_page - 1) * page.per_page + 1;
        const to = Math.min(page.current_page * page.per_page, page.total);

        return {
            data: page.data,
            current_page: page.current_page,
            per_page: page.per_page,
            total: page.total,
            last_page: lastPage,
            from,
            to,
            path: '',
            next_page_url: '',
        };
    }

    protected async fetchList(args: IFetchListArgs): Promise<IPagedData<IBulkRmaActionRequestSummary> | undefined> {
        if (this.storeId == null) {
            return undefined;
        }

        try {
            const response = await this.troubleshootingService.listBulkRmaActionRequests(this.storeId, {
                status: this.statusFilter || undefined,
                page: args.page,
                perPage: args.perPage,
            });
            runInAction(() => {
                this.error = null;
            });
            return this.mapRequestsPageToPagedData(response);
        } catch (err: any) {
            runInAction(() => {
                this.error = err?.response?.data?.error ?? err?.message ?? 'Failed to load bulk requests';
            });
            return undefined;
        }
    }
}

export default new BulkRmaActionRequestsPagedListStore();
